import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle2, XCircle, Sun } from "lucide-react";

const eur = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

export default function PaymentResult({ mode }) {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState(mode === "cancel" ? "cancel" : "checking");
  const [info, setInfo] = useState({});
  const attempts = useRef(0);

  useEffect(() => {
    if (mode === "cancel") return;
    const sid = params.get("session_id");
    if (!sid) { setStatus("error"); return; }
    let stop = false;
    const poll = async () => {
      if (stop) return;
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") { setInfo(data); setStatus("paid"); return; }
        if (data.status === "expired" || attempts.current > 15) { setStatus("error"); return; }
      } catch {
        if (attempts.current > 15) { setStatus("error"); return; }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stop = true; };
  }, [mode, params]);

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6 text-center">
      <div className="flex items-center gap-2 mb-8">
        <div className="h-9 w-9 rounded-full overflow-hidden flex items-center justify-center bg-white"><img src="/torri-icon.png" alt="TORRI.SOLAR" className="h-9 w-9 object-contain" /></div>
        <div className="font-heading font-extrabold tracking-wide">TORRI<span style={{ color: "#FDD003" }}>.SOLAR</span></div>
      </div>
      {status === "checking" && (
        <div data-testid="payment-checking">
          <Loader2 className="h-10 w-10 animate-spin text-primary mx-auto mb-4" />
          <h1 className="text-xl font-heading font-bold">Conferma del pagamento in corso…</h1>
          <p className="text-sm text-muted-foreground mt-2">Stiamo verificando la transazione con Stripe. Non chiudere questa pagina.</p>
        </div>
      )}
      {status === "paid" && (
        <div data-testid="payment-success">
          <CheckCircle2 className="h-14 w-14 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold">Caparra pagata con successo</h1>
          <p className="text-sm text-muted-foreground mt-2">Ordine confermato. Importo caparra: <b>{eur(info.amount)}</b>.{info.commessa ? <> Commessa <b>{info.commessa}</b>.</> : null}</p>
          <p className="text-xs text-muted-foreground mt-3 max-w-md mx-auto leading-snug" data-testid="caparra-terms-result">La caparra è stata versata a titolo di conferma dell'ordine e verrà interamente scomputata dal saldo finale (non è un costo aggiuntivo).</p>
          <Button className="mt-6 font-semibold" onClick={() => navigate("/leads")} data-testid="back-to-leads-btn">Torna ai lead</Button>
        </div>
      )}
      {status === "cancel" && (
        <div data-testid="payment-cancel">
          <XCircle className="h-14 w-14 text-muted-foreground mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold">Pagamento annullato</h1>
          <p className="text-sm text-muted-foreground mt-2">Nessun addebito effettuato. Puoi riprovare quando vuoi.</p>
          <Button variant="outline" className="mt-6 font-semibold" onClick={() => navigate(-1)} data-testid="retry-btn">Indietro</Button>
        </div>
      )}
      {status === "error" && (
        <div data-testid="payment-error">
          <XCircle className="h-14 w-14 text-destructive mx-auto mb-4" />
          <h1 className="text-2xl font-heading font-bold">Verifica non riuscita</h1>
          <p className="text-sm text-muted-foreground mt-2">Non è stato possibile confermare il pagamento. Se hai completato il pagamento, controlla lo stato del lead o riprova.</p>
          <Button variant="outline" className="mt-6 font-semibold" onClick={() => navigate("/leads")}>Torna ai lead</Button>
        </div>
      )}
    </div>
  );
}
